import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  ApiError,
  fetchMyAppointments,
  fetchReviews,
  submitReview,
  type Appointment,
  type Review,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { formatSalon } from '../lib/datetime';
import StarRating from './StarRating';

interface Props {
  slug: string;
  rating: number;
  reviewCount: number;
}

export default function ReviewsSection({ slug, rating, reviewCount }: Props) {
  const { session } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(reviewCount);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // A completed, not-yet-reviewed appointment the customer can review.
  const [reviewable, setReviewable] = useState<Appointment | null>(null);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isCustomer = session?.role === 'customer';

  useEffect(() => {
    setLoading(true);
    fetchReviews(slug)
      .then((data) => {
        setReviews(data.results);
        setCount(data.count);
        setPage(1);
        setHasMore(data.next !== null);
      })
      .catch(() => {
        setReviews([]);
        setHasMore(false);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchReviews(slug, page + 1);
      // A review posted meanwhile shifts pages by one; skip anything already shown.
      setReviews((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...data.results.filter((r) => !seen.has(r.id))];
      });
      setPage((p) => p + 1);
      setHasMore(data.next !== null);
    } catch {
      // Keep the button so the visitor can retry.
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isCustomer) {
      setReviewable(null);
      return;
    }
    fetchMyAppointments()
      .then((appts) => {
        const found = appts.find(
          (a) => a.tenantSlug === slug && a.status === 'completed' && !a.hasReview,
        );
        setReviewable(found ?? null);
      })
      .catch(() => setReviewable(null));
  }, [slug, isCustomer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewable) return;
    setSubmitting(true);
    setError('');
    try {
      const created = await submitReview(slug, {
        appointmentId: reviewable.id,
        rating: stars,
        comment: comment.trim(),
      });
      setReviews((prev) => [created, ...prev]);
      setCount((c) => c + 1);
      setReviewable(null);
      setComment('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit your review.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews">
      <div className="mb-6 flex items-center gap-3">
        <h2 className="font-display text-fg text-2xl font-semibold">Reviews</h2>
        <span className="text-fg-muted flex items-center gap-1.5 text-sm">
          <StarRating value={Math.round(rating)} />
          {Number(rating).toFixed(1)} · {count} {count === 1 ? 'review' : 'reviews'}
        </span>
      </div>

      {reviewable && (
        <form
          onSubmit={handleSubmit}
          className="border-default bg-canvas mb-6 rounded-2xl border p-5"
        >
          <h3 className="text-fg mb-1 font-semibold">Leave a review</h3>
          <p className="text-fg-muted mb-3 text-sm">
            How was your {reviewable.serviceName} appointment?
          </p>
          {error && (
            <div className="border-danger/25 bg-danger-soft text-danger mb-3 rounded-xl border p-2 text-sm">
              {error}
            </div>
          )}
          <StarRating value={stars} onChange={setStars} size={28} />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="Tell others about your experience (optional)"
            className="focus:border-brand-600 focus:ring-brand-600 border-strong mt-3 block w-full rounded-xl border px-4 py-2.5 text-sm shadow-sm outline-none focus:ring-1"
          />
          <button
            type="submit"
            disabled={submitting}
            className="bg-brand-600 hover:bg-brand-700 mt-3 flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit review
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-fg-subtle py-6 text-center">
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-fg-muted text-sm">No reviews yet — be the first to leave one.</p>
      ) : (
        <ul className="space-y-4">
          {reviews.map((r) => (
            <li key={r.id} className="border-subtle bg-surface rounded-2xl border p-4">
              <div className="flex items-center justify-between">
                <span className="text-fg font-medium">{r.customerName}</span>
                <StarRating value={r.rating} />
              </div>
              <p className="text-fg-subtle mt-1 text-xs">
                {formatSalon(new Date(r.createdAt), 'MMMM d, yyyy')}
              </p>
              {r.comment && <p className="text-fg-secondary mt-2 text-sm">{r.comment}</p>}
            </li>
          ))}
        </ul>
      )}

      {!loading && hasMore && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="border-strong text-fg hover:bg-surface mx-auto mt-6 flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50"
        >
          {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
          Load more reviews
        </button>
      )}
    </section>
  );
}
